import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFamilyTheme } from '../context/FamilyThemeContext';
import {
  FaCalendarAlt, FaArrowLeft, FaBook, FaChevronDown,
  FaPlay, FaCheckSquare, FaCommentAlt, FaDownload, FaMapMarkerAlt,
} from 'react-icons/fa';
import { TooltipProvider } from './ui/tooltip';
import { BrandLogo } from './BrandLogo';
import axios from 'axios';
import { toast } from 'sonner';
import { DashboardNavItems, AdminButton } from './header/DashboardNavItems';
import { FamilyNavItems } from './header/FamilyNavItems';
import { EventNavItems } from './header/EventNavItems';
import { MobileNavMenu, useIsDesktop } from './header/MobileNavMenu';

const API = '';

/**
 * Reusable Header Component for Legacy Forge Family
 *
 * Props:
 * - variant: 'dashboard' | 'family' | 'event' - determines which navigation items to show
 * - family: (for 'family' and 'event' variants) the family object with name and members
 * - familyId: (for 'family' variant) the family ID for navigation
 * - event: (for 'event' variant) the event object
 * - eventActions: (for 'event' variant) object with action handlers { onShare, onInvite, onEdit, onDelete, canEdit }
 */
function NavItems({ variant, user, handleLogout, family, familyId, groupAccount, hideAdmin }) {
  return variant === 'dashboard'
    ? <DashboardNavItems user={user} handleLogout={handleLogout} groupAccount={groupAccount} hideAdmin={hideAdmin} />
    : <FamilyNavItems family={family} familyId={familyId} userId={user?.user_id} />;
}

function Header({ variant = 'dashboard', family = null, familyId = null, event = null, eventActions = null }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const isDesktop = useIsDesktop();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Trigger the global help button's menu
  const triggerGlobalHelp = (action) => {
    setShowHelpMenu(false);
    // Dispatch a custom event that GlobalHelpButton listens for
    window.dispatchEvent(new CustomEvent('openHelpAction', { detail: { action } }));
  };

  // Get ancestral colors from global theme context (persists across pages)
  const { ancestralColors: globalColors } = useFamilyTheme();

  // Use family-specific colors if on family page, otherwise use global theme
  const ancestralColors = family?.ancestral_colors || globalColors;
  const primaryColor = ancestralColors?.primary || '#3B82F6';
  const secondaryColor = ancestralColors?.secondary || '#4F46E5';

  // Dynamic gradient style for family badge - show on all pages when colors are set
  const hasColors = !!ancestralColors;
  const badgeStyle = hasColors
    ? { background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` }
    : {};

  return (
    <header
      className="bg-white shadow-sm"
      data-testid="app-header"
      style={hasColors ? {
        borderBottom: `3px solid ${primaryColor}`
      } : {}}
    >
      <div className="container mx-auto px-4 py-4">
        <div className={`flex items-center ${variant === 'event' ? 'flex-wrap gap-y-3' : ''} justify-between`}>
          {/* Left Section: Logo and Branding */}
          <div className={`flex items-center space-x-3 sm:space-x-4 min-w-0 ${variant === 'event' ? 'flex-shrink-0' : '2xl:flex-shrink-0'}`}>
            {/* Circular Badge Logo */}
            <Link
              to={variant === 'family' ? '/dashboard' : '/'}
              className="group flex items-center space-x-3 hover:opacity-90 transition"
              data-testid="logo-link"
            >
              <div
                className="w-11 h-11 sm:w-14 sm:h-14 rounded-xl p-[3px] bg-gradient-to-br from-blue-600 to-indigo-700 shadow-lg group-hover:shadow-xl transition-shadow flex-shrink-0"
                style={badgeStyle}
              >
                <BrandLogo className="w-full h-full" testId="header-logo" />
              </div>
              <span className={`font-bold text-gray-800 whitespace-nowrap hidden sm:inline ${variant === 'family' ? 'text-xl' : 'text-xl md:text-2xl'}`}>
                Legacy Forge Family
              </span>
            </Link>

            {/* Help & Guides Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowHelpMenu(!showHelpMenu)}
                className="flex items-center space-x-1 px-3 py-1.5 text-white rounded-full shadow hover:shadow-md hover:scale-105 transition-all duration-200"
                style={hasColors ? {
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`
                } : {
                  background: 'linear-gradient(to right, #3B82F6, #4F46E5)'
                }}
                data-testid="header-help-btn"
              >
                <FaBook className="text-sm" />
                <span className="font-medium text-sm">Help</span>
                <FaChevronDown className={`text-xs transition-transform ${showHelpMenu ? 'rotate-180' : ''}`} />
              </button>

              {/* Help Dropdown Menu */}
              {showHelpMenu && (
                <>
                  {/* Backdrop to close menu */}
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowHelpMenu(false)}
                  />
                  <div className="absolute top-full left-0 mt-2 z-50 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden w-64">
                    <div className="p-2 space-y-1">
                      <button
                        onClick={() => triggerGlobalHelp('journey')}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-blue-50 rounded-lg transition"
                        data-testid="header-legacy-journey-btn"
                      >
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <FaPlay className="text-green-600 text-sm" />
                        </div>
                        <div>
                          <span className="font-semibold text-gray-900">Legacy Journey</span>
                          <p className="text-xs text-gray-500">Guided tour of the app</p>
                        </div>
                      </button>
                      <button
                        onClick={() => triggerGlobalHelp('checklist')}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-yellow-50 rounded-lg transition"
                        data-testid="header-getting-started-btn"
                      >
                        <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                          <FaCheckSquare className="text-yellow-600 text-sm" />
                        </div>
                        <div>
                          <span className="font-semibold text-gray-900">Getting Started</span>
                          <p className="text-xs text-gray-500">Quick setup checklist</p>
                        </div>
                      </button>
                      <Link
                        to="/help-center"
                        onClick={() => setShowHelpMenu(false)}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-blue-50 rounded-lg transition"
                        data-testid="header-help-center-link"
                      >
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <FaBook className="text-blue-600 text-sm" />
                        </div>
                        <div>
                          <span className="font-semibold text-gray-900">Help Center</span>
                          <p className="text-xs text-gray-500">Full feature guide</p>
                        </div>
                      </Link>

                      {/* Divider */}
                      <div className="border-t border-gray-100 my-2"></div>

                      {/* Feedback Button */}
                      <button
                        onClick={() => {
                          setShowHelpMenu(false);
                          window.dispatchEvent(new CustomEvent('openFeedback'));
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-purple-50 rounded-lg transition"
                        data-testid="header-feedback-btn"
                      >
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                          <FaCommentAlt className="text-purple-600 text-sm" />
                        </div>
                        <div>
                          <span className="font-semibold text-gray-900">Send Feedback</span>
                          <p className="text-xs text-gray-500">Help us improve!</p>
                        </div>
                      </button>

                      {/* System Health Check */}
                      <Link
                        to="/health-check"
                        onClick={() => setShowHelpMenu(false)}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-green-50 rounded-lg transition"
                        data-testid="header-health-check-link"
                      >
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <FaCheckSquare className="text-green-600 text-sm" />
                        </div>
                        <div>
                          <span className="font-semibold text-gray-900">System Health</span>
                          <p className="text-xs text-gray-500">Post-deploy status check</p>
                        </div>
                      </Link>

                      {/* Export Data - Only show when viewing a family */}
                      {variant === 'family' && family && (
                        <button
                          onClick={async () => {
                            setShowHelpMenu(false);
                            const token = localStorage.getItem('session_token');
                            if (!token || !family?.family_id) {
                              toast.error('Please select a family first');
                              return;
                            }
                            try {
                              toast.info('Preparing your export...');
                              const response = await axios.get(
                                `${API}/api/export/families/${family.family_id}?format=json&include_photos=true`,
                                {
                                  headers: { Authorization: `Bearer ${token}` },
                                  responseType: 'blob'
                                }
                              );
                              const blob = new Blob([response.data], { type: 'application/json' });
                              const url = window.URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = url;
                              const safeName = (family.name || 'family').replace(/[^a-zA-Z0-9 -_]/g, '').substring(0, 30);
                              const date = new Date().toISOString().split('T')[0];
                              link.download = `${safeName}_export_${date}.json`;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              window.URL.revokeObjectURL(url);
                              toast.success('Family data exported!');
                            } catch (error) {
                              console.error('Export error:', error);
                              toast.error('Failed to export data');
                            }
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-cyan-50 rounded-lg transition"
                          data-testid="header-export-data-btn"
                        >
                          <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center">
                            <FaDownload className="text-cyan-600 text-sm" />
                          </div>
                          <div>
                            <span className="font-semibold text-gray-900">Export My Data</span>
                            <p className="text-xs text-gray-500">Download family data</p>
                          </div>
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Family-specific: Back button and family name */}
            {variant === 'family' && family && (
              <>
                <span className="text-gray-300 hidden sm:inline">|</span>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="text-gray-600 hover:text-gray-800"
                  data-testid="back-to-dashboard-btn"
                >
                  <FaArrowLeft className="text-lg" />
                </button>
                <div className="min-w-0">
                  <h1 className="text-base sm:text-lg font-semibold text-gray-800 truncate max-w-[40vw] sm:max-w-none">{family?.name}</h1>
                </div>
              </>
            )}

            {/* Event-specific: Back button and event info */}
            {variant === 'event' && event && (
              <>
                <span className="text-gray-300 hidden sm:inline">|</span>
                <button
                  onClick={() => navigate(-1)}
                  className="text-gray-600 hover:text-gray-800"
                  data-testid="back-btn"
                >
                  <FaArrowLeft className="text-lg" />
                </button>
                <div className="min-w-0 max-w-[200px] sm:max-w-[300px] md:max-w-none md:flex-1">
                  <h1 className="text-lg sm:text-xl font-bold text-gray-800 truncate">{event?.name}</h1>
                  <div className="flex items-center space-x-2 sm:space-x-4 mt-1 text-xs sm:text-sm text-gray-600">
                    <div className="flex items-center space-x-1">
                      <FaCalendarAlt className="text-xs" />
                      <span className="whitespace-nowrap">
                        {event?.start_date && (
                          <>
                            {new Date(event.start_date).toLocaleDateString()}
                            {event?.end_date && event.end_date !== event.start_date && (
                              <span className="hidden sm:inline"> - {new Date(event.end_date).toLocaleDateString()}</span>
                            )}
                          </>
                        )}
                        {!event?.start_date && event?.date}
                      </span>
                    </div>
                    {event?.location && (
                      <div className="flex items-center space-x-1 hidden md:flex">
                        <FaMapMarkerAlt className="text-xs" />
                        <span className="truncate max-w-[150px]">{event.location}</span>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Right Section: Navigation Items */}
          <TooltipProvider delayDuration={200}>
            <div className={`flex items-center ${variant === 'event' ? 'flex-wrap gap-2 justify-end w-full sm:w-auto' : 'space-x-4'}`}>
              {variant === 'event' ? (
                <EventNavItems event={event} family={family} eventActions={eventActions} />
              ) : isDesktop ? (
                <>
                  {variant === 'family' && user?.is_platform_admin && <AdminButton />}
                  <NavItems variant={variant} user={user} handleLogout={handleLogout} family={family} familyId={familyId} groupAccount />
                </>
              ) : (
                <div className="flex items-center gap-2">
                  {user?.is_platform_admin && <AdminButton />}
                  <MobileNavMenu label="Menu">
                    <NavItems variant={variant} user={user} handleLogout={handleLogout} family={family} familyId={familyId} hideAdmin />
                  </MobileNavMenu>
                </div>
              )}
            </div>
          </TooltipProvider>
        </div>
      </div>
    </header>
  );
}

export default Header;
